/**
 * <ladd-toc>: "On this page" index.
 * - Links scroll to their chapter (smooth unless the visitor prefers less motion).
 * - The chapter in view is marked with aria-current.
 * - With data-build="h2", the index is built from the h2 headings inside [data-toc-source]
 *   (used for Shopify policies, whose headings come from the admin).
 */
class LaddToc extends HTMLElement {
  connectedCallback() {
    if (this.dataset.build) this.#build();
    this.links = [...this.querySelectorAll('a[href^="#"]')];
    this.targets = this.links.map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))));
    this.addEventListener('click', this.#onClick);
    this.#observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #build() {
    const source = document.querySelector(this.dataset.source || '[data-toc-source]');
    const list = this.querySelector('[data-toc-list]');
    if (!source || !list) return;
    const headings = [...source.querySelectorAll(this.dataset.build)];
    headings.forEach((heading, index) => {
      const number = String(index + 1).padStart(2, '0');
      if (!heading.id) heading.id = `section-${number}-${heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      heading.dataset.number = number;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.className = 'ladd-toc__link';
      link.innerHTML = `<span class="ladd-toc__number" aria-hidden="true">${number}</span>`;
      link.append(heading.textContent.trim());
      item.append(link);
      list.append(item);
    });
    if (headings.length === 0) this.hidden = true;
  }

  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!link) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    history.replaceState(null, '', link.hash);
  };

  #observe() {
    const visible = new Map();
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) visible.set(entry.target, entry.isIntersecting);
        const current = this.targets.find((target) => target && visible.get(target));
        if (!current) return;
        this.links.forEach((link, index) => {
          if (this.targets[index] === current) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      },
      { rootMargin: '-25% 0px -60% 0px' }
    );
    this.targets.forEach((target) => target && this.observer.observe(target));
  }
}

if (!customElements.get('ladd-toc')) {
  customElements.define('ladd-toc', LaddToc);
}
